import { Button, Icon } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import '../../../asset/css/webcam.css'
import useWindowSize from '../WebcamCapture/hooks/useWindowSize'
import Visualizer from '../Visualizer/Visualizer'
import PlayerVisualizer from '../Visualizer/PlayerVisualizer'
import getSupportedRecorderMimeType from './getSupportedRecorderMimeType'

type AudioStreamCaptureProps = {
  onSuccess: (audioUrl: string) => void
  onCancel: () => void
}

const AudioStreamCapture = ({
  onSuccess,
  onCancel
}: AudioStreamCaptureProps) => {
  const { t } = useTranslation()
  const size = useWindowSize()

  const [capturing, setCapturing] = useState(false)
  const [url, setUrl] = useState('')

  const streamRef = useRef<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const urlRef = useRef('')
  const handedOff = useRef(false)

  const mimeType = getSupportedRecorderMimeType()

  useEffect(() => {
    let cancelled = false

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
      })
      .catch(() => {
        if (!cancelled) onCancel()
      })

    return () => {
      cancelled = true
      streamRef.current?.getTracks().forEach((track) => track.stop())
      if (urlRef.current && !handedOff.current) {
        URL.revokeObjectURL(urlRef.current)
      }
    }
  }, [])

  const handleStartCapture = useCallback(() => {
    const stream = streamRef.current
    if (!stream) return

    chunksRef.current = []

    try {
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType
      })
    } catch {
      onCancel()
      return
    }

    setCapturing(true)

    mediaRecorderRef.current.ondataavailable = ({ data }) => {
      if (data.size > 0) {
        chunksRef.current.push(data)
      }
    }

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType })
      const objectUrl = URL.createObjectURL(blob)
      urlRef.current = objectUrl
      handedOff.current = false
      setUrl(objectUrl)
    }

    mediaRecorderRef.current.start()
  }, [])

  const handleStopCapture = useCallback(() => {
    mediaRecorderRef.current?.stop()
    setCapturing(false)
  }, [])

  const handleDiscard = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = ''
    }
    setUrl('')
    chunksRef.current = []
  }

  const handleUse = () => {
    handedOff.current = true
    onSuccess(url)
  }

  return (
    <div
      className='webcam'
      style={{
        width: size.width,
        height: size.height,
        background: '#ffffff',
        position: 'fixed',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        zIndex: 2
      }}
    >
      <div className='position-relative h-100'>
        <div className='webcam-header' style={{ background: '#ffffff' }}>
          <div className='webcam-header-elements'>
            <button type='button' className='icon-button' onClick={onCancel}>
              <Icon iconName='Close' icSize='small' />
            </button>
            <span>
              {url
                ? t('activity-document-audio.recorder.preview')
                : capturing
                  ? t('activity-document-audio.recorder.recording')
                  : t('activity-document-audio.recorder.ready')}
            </span>
          </div>
        </div>

        <div
          style={{
            paddingTop: '50px',
            paddingBottom: '130px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {url ? <PlayerVisualizer url={url} /> : capturing && <Visualizer />}
        </div>

        <div className='webcam-footer' style={{ background: '#ffffff' }}>
          {url ? (
            <div className='webcam-footer-controls webcam-footer-controls-after-capture'>
              <Button onClick={handleUse}>
                {t('activity-document-audio.recorder.use-this-audio')}
              </Button>
              <Button variant='secondary' onClick={handleDiscard}>
                {t('activity-document-audio.recorder.discard')}
              </Button>
            </div>
          ) : (
            <div className='webcam-footer-controls webcam-footer-controls-before-capture'>
              <Button
                type='button'
                onClick={capturing ? handleStopCapture : handleStartCapture}
                backgroundColor='#dc3545'
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: `50%`,
                  border: '4px solid white'
                }}
              >
                <Icon
                  iconName={capturing ? 'Pause' : 'TakeAudio'}
                  icSize='regular'
                />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudioStreamCapture
